import { useRef, useEffect, useState } from 'react';

export function useFadeUp() {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) el.classList.add('visible'); },
      { threshold: 0, rootMargin: '0px 0px 100px 0px' }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return ref;
}

export function FadeUp({ children, delay = 0, style = {} }) {
  const ref = useFadeUp();
  return (
    <div ref={ref} className="fade-up" style={{ transitionDelay: `${delay}ms`, ...style }}>
      {children}
    </div>
  );
}

export function useGalleries() {
  const [data, setData] = useState([]);

  useEffect(() => {
    let active = true;

    const loadGalleries = () => {
      const url = `${process.env.PUBLIC_URL}/data/galleries.json?v=${Date.now()}`;
      fetch(url, { cache: 'no-store' })
        .then(r => {
          if (!r.ok) throw new Error('Unable to load galleries');
          return r.json();
        })
        .then(json => {
          if (active) setData(json || []);
        })
        .catch(() => {
          if (active) setData([]);
        });
    };

    const loadWhenVisible = () => {
      if (document.visibilityState === 'visible') loadGalleries();
    };

    loadGalleries();
    window.addEventListener('focus', loadGalleries);
    document.addEventListener('visibilitychange', loadWhenVisible);

    return () => {
      active = false;
      window.removeEventListener('focus', loadGalleries);
      document.removeEventListener('visibilitychange', loadWhenVisible);
    };
  }, []);

  return data;
}

export function useGallery(slug) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!slug) {
      setLoading(false);
      return;
    }

    let active = true;

    const loadGallery = () => {
      const url = `${process.env.PUBLIC_URL}/data/galleries.json?v=${Date.now()}`;
      fetch(url, { cache: 'no-store' })
        .then(r => {
          if (!r.ok) throw new Error('Unable to load galleries');
          return r.json();
        })
        .then(json => {
          if (!active) return;
          const gallery = (json || []).find(g => g.slug === slug);
          setData(gallery || null);
          setLoading(false);
        })
        .catch(() => {
          if (!active) return;
          setData(null);
          setLoading(false);
        });
    };

    const loadWhenVisible = () => {
      if (document.visibilityState === 'visible') loadGallery();
    };

    setData(null);
    setLoading(true);
    loadGallery();
    window.addEventListener('focus', loadGallery);
    document.addEventListener('visibilitychange', loadWhenVisible);

    return () => {
      active = false;
      window.removeEventListener('focus', loadGallery);
      document.removeEventListener('visibilitychange', loadWhenVisible);
    };
  }, [slug]);

  return { data, loading };
}

export function useServiceImages(slug) {
  const [images, setImages] = useState([]);

  useEffect(() => {
    if (!slug) return;

    const categoryByService = {
      repairs: 'repairs_renovation',
      'functional-art': 'custom_installations',
      structural: 'structural_work',
    };
    const category = categoryByService[slug];
    if (!category) {
      setImages([]);
      return;
    }

    let active = true;
    setImages([]);

    fetch(`${process.env.PUBLIC_URL}/data/galleries.json?v=${Date.now()}`, { cache: 'no-store' })
      .then(r => {
        if (!r.ok) throw new Error('Unable to load gallery data');
        return r.json();
      })
      .then(galleries => {
        if (!active) return;
        const categoryImages = (galleries || [])
          .filter(gallery => gallery.category === category)
          .flatMap(gallery => (gallery.images || []).slice(0, 3));
        setImages(categoryImages);
      })
      .catch(() => {
        if (active) setImages([]);
      });

    return () => { active = false; };
  }, [slug]);

  return images;
}
